import { useMemo, useState } from 'react';
import {
  setTovQueryData,
  useTovMutation,
  useTovQuery,
  useTovQueryClient,
} from '@tov-plus/frontend-data';
import {
  TovButton,
  PageCard,
  PageEyebrow,
  PageGrid,
  useToast,
  msg,
} from '@tov-plus/ui';
import { guestApi } from '../app/api';
import { StatePanel } from '../components/StatePanel';
import { useGuestAccess } from '../contexts/GuestAccessContext';
import { guestQueryKeys } from '../lib/data/guestQueryKeys';
import { getGuestErrorMessage } from '../lib/errors/guestErrors';
import {
  RegistryFilterCard,
  RegistryHero,
  RegistryItemCard,
} from './RegistryPageSections';

const registryErrorMessage = () =>
  msg('pages.registry_page.registry_load_error');

export function RegistryPage() {
  const access = useGuestAccess();
  const notifications = useToast();
  const queryClient = useTovQueryClient();
  const [availabilityFilter, setAvailabilityFilter] = useState<
    'all' | 'available'
  >('available');
  const registryQueryKey =
    access.status === 'ready'
      ? guestQueryKeys.registry(access.occasionSlug, access.guestToken)
      : guestQueryKeys.registry('unknown', 'unknown');
  const registryQuery = useTovQuery({
    queryKey: registryQueryKey,
    queryFn: () =>
      guestApi.getRegistry(
        access.status === 'ready' ? access.occasionSlug : '',
        access.status === 'ready' ? access.guestToken : '',
      ),
    enabled: access.status === 'ready',
  });
  const registry = registryQuery.data ?? null;
  const invalidateRegistry =
    access.status === 'ready' ? [registryQueryKey] : [];
  const claimRegistryItem = useTovMutation({
    mutationFn: async (input: {
      itemId: string;
      claimInput: Parameters<typeof guestApi.claimRegistryItem>[3];
    }) => {
      if (access.status !== 'ready') {
        throw new Error(registryErrorMessage());
      }

      return guestApi.claimRegistryItem(
        access.occasionSlug,
        access.guestToken,
        input.itemId,
        input.claimInput,
      );
    },
    onSuccess(next) {
      setTovQueryData(queryClient, registryQueryKey, next);
      notifications.success(msg('pages.registry_page.registry_claim_saved'));
    },
    onError(error) {
      notifications.error(getGuestErrorMessage(error, registryErrorMessage()));
    },
    invalidate: invalidateRegistry,
  });
  const unclaimRegistryItem = useTovMutation({
    mutationFn: async (itemId: string) => {
      if (access.status !== 'ready') {
        throw new Error(registryErrorMessage());
      }

      return guestApi.unclaimRegistryItem(
        access.occasionSlug,
        access.guestToken,
        itemId,
      );
    },
    onSuccess(next) {
      setTovQueryData(queryClient, registryQueryKey, next);
      notifications.success(msg('pages.registry_page.registry_claim_released'));
    },
    onError(error) {
      notifications.error(getGuestErrorMessage(error, registryErrorMessage()));
    },
    invalidate: invalidateRegistry,
  });
  const visibleItems = useMemo(() => {
    if (!registry) {
      return [];
    }
    return registry.items.filter((entry) =>
      availabilityFilter === 'all'
        ? true
        : entry.analytics.remainingQuantity > 0,
    );
  }, [availabilityFilter, registry]);
  if (access.status !== 'ready') {
    return null;
  }
  if (registryQuery.isLoading) {
    return <StatePanel preset="loading" />;
  }
  if (registryQuery.isError) {
    return (
      <StatePanel
        preset="loading"
        actions={
          <TovButton onClick={() => void registryQuery.refetch()}>
            {msg('general.actions.retry')}
          </TovButton>
        }
      />
    );
  }
  if (!registry || registry.items.length === 0) {
    return <StatePanel preset="empty" />;
  }
  return (
    <PageGrid>
      <RegistryHero registry={registry} />
      <RegistryFilterCard
        availabilityFilter={availabilityFilter}
        onAvailabilityFilterChange={setAvailabilityFilter}
      />
      <PageCard wide>
        <PageEyebrow>
          {msg('pages.registry_page.pages_registry_page_after_the_event')}
        </PageEyebrow>
        <p>
          {msg(
            'pages.registry_page.pages_registry_page_registry_gallery_and_follow_up_messages_can_now',
          )}{' '}
        </p>
      </PageCard>
      {visibleItems.map((entry) => (
        <RegistryItemCard
          key={entry.item.id}
          entry={entry}
          onClaim={async (input) => {
            claimRegistryItem.mutate({
              itemId: entry.item.id,
              claimInput: input,
            });
          }}
          onUnclaim={async () => {
            unclaimRegistryItem.mutate(entry.item.id);
          }}
        />
      ))}
    </PageGrid>
  );
}
